import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import MainLayout from 'src/pages/private';
import { CircularProgress } from '@mui/material';
import DateRangeSelector, {
  DateRange,
} from 'src/components/DateSelector/DateRangeSelector';
import { useGetDashboardSummary } from 'src/services/report.service';
import {
  DashboardContainer,
  DashboardHeader,
  Title,
  HeaderActions,
  ActionButton,
  StatsGrid,
  ChartGrid,
  CardContainer,
  StatCardWrapper,
  StatContentWrapper,
  StatTitle,
  StatValue,
  PercentageBadge,
  PercentageSubtext,
  ChartTitle,
  WaveBackground,
  TableGrid,
  StyledTable,
  TableHeader,
  TableRow,
  StatusBadge,
  LoadingContainer,
  StatUnit,
  ChartBox,
  TableCard,
  TableHeaderWrapper,
  ScrollableTableContainer,
  TableHeaderCell,
  TableDataCell,
  TooltipWrapper,
  TooltipLabel,
  TooltipItem,
  TooltipMarker,
} from './Dashboard.styled';
import { formatCurrency } from 'src/utils/format.utils';
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const ORDER_STATUS_DATA = [
  { name: 'Payment Pending', value: 35 },
  { name: 'Completed', value: 50 },
  { name: 'Cancelled', value: 15 },
];

const STATUS_COLOURS = [
  '#1d4ed8', // Blue
  '#15803d', // Green
  '#FFA500', // Orange
  '#FF0000', // Red
  '#CBC3E3', // Purple
  '#FFC0CB', // Pink
  '#808080', // Grey
];

const WaveSVG = ({ color }: { color: string }) => (
  <WaveBackground viewBox="0 0 100 30" preserveAspectRatio="none">
    <path d="M0 30 V15 Q25 5 50 15 T100 15 V30 z" fill={color} opacity="0.3" />
  </WaveBackground>
);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <TooltipWrapper elevation={0}>
        <TooltipLabel>{label}</TooltipLabel>
        {payload.map((item: any, index: number) => (
          <TooltipItem key={index}>
            <TooltipMarker bgColor={item.color || item.payload.fill} />
            <span>{item.name}:</span>
            <span className="value">{formatCurrency(item.value)}</span>
          </TooltipItem>
        ))}
      </TooltipWrapper>
    );
  }
  return null;
};

const Dashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: dayjs().startOf('month'),
    endDate: dayjs().endOf('day'),
    preset: 'This Month',
  });

  const { loading, summaryData, fetchDashboardSummary } =
    useGetDashboardSummary();

  const handleRangeChange = (range: DateRange) => {
    setDateRange(range);
    fetchDashboardSummary({
      startDate: range.startDate?.format('YYYY-MM-DD'),
      endDate: range.endDate?.format('YYYY-MM-DD'),
    });
  };

  useEffect(() => {
    fetchDashboardSummary({
      startDate: dateRange.startDate?.format('YYYY-MM-DD'),
      endDate: dateRange.endDate?.format('YYYY-MM-DD'),
    });
    // eslint-disable-next-line
  }, []);

  const summary = summaryData?.summary || {
    totalOrders: 0,
    revenue: 0,
    received: 0,
    outstanding: 0,
    production: 0,
    expenses: 0,
  };

  const orderStatusData = summaryData?.charts?.orderStatus || [];
  const revenueOutstandingData =
    summaryData?.charts?.revenueVsOutstanding || [];
  const recentOrders = summaryData?.recentOrders || [];
  const topCustomers = summaryData?.topCustomers || [];
  const supplierPayments = summaryData?.supplierPayments || [];
  const recentVouchers = summaryData?.recentVouchers || [];

  if (loading) {
    return (
      <MainLayout title="Dashboard">
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Dashboard">
      <DashboardContainer>
        <DashboardHeader>
          <HeaderActions>
            <DateRangeSelector
              defaultPreset={dateRange.preset}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onRangeChange={handleRangeChange}
            />
            <ActionButton
              variant="outlined"
              endIcon={<i className="bx bx-chevron-down" />}
              disabled
            >
              Export
            </ActionButton>
            <ActionButton
              variant="contained"
              color="primary"
              endIcon={<i className="bx bx-caret-down" />}
              disabled
            >
              + Create
            </ActionButton>
          </HeaderActions>
        </DashboardHeader>

        <StatsGrid>
          <StatCardWrapper elevation={0}>
            <StatContentWrapper>
              <StatTitle>Total Orders</StatTitle>
              <StatValue>{summary.totalOrders.toLocaleString()}</StatValue>
            </StatContentWrapper>
            <WaveSVG color="#3b82f6" />
          </StatCardWrapper>

          <StatCardWrapper elevation={0}>
            <StatContentWrapper>
              <StatTitle>Revenue</StatTitle>
              <StatValue>{formatCurrency(summary.revenue)}</StatValue>
            </StatContentWrapper>
            <WaveSVG color="#10b981" />
          </StatCardWrapper>

          <StatCardWrapper elevation={0}>
            <StatContentWrapper>
              <StatTitle>Received</StatTitle>
              <StatValue>{formatCurrency(summary.received)}</StatValue>
            </StatContentWrapper>
            <WaveSVG color="#10b981" />
          </StatCardWrapper>

          <StatCardWrapper elevation={0}>
            <StatContentWrapper>
              <StatTitle>Outstanding</StatTitle>
              <StatValue colorType="error">
                {formatCurrency(summary.outstanding)}
              </StatValue>
            </StatContentWrapper>
            <WaveSVG color="#ef4444" />
          </StatCardWrapper>

          <StatCardWrapper elevation={0}>
            <StatContentWrapper>
              <StatTitle>Production</StatTitle>
              <StatValue>
                {summary.production.toLocaleString()} <StatUnit>Units</StatUnit>
              </StatValue>
            </StatContentWrapper>
            <WaveSVG color="#8b5cf6" />
          </StatCardWrapper>

          <StatCardWrapper elevation={0}>
            <StatContentWrapper>
              <StatTitle>Expenses</StatTitle>
              <StatValue>{formatCurrency(summary.expenses)}</StatValue>
            </StatContentWrapper>
            <WaveSVG color="#f59e0b" />
          </StatCardWrapper>
        </StatsGrid>

        <ChartGrid>
          <CardContainer elevation={0}>
            <ChartTitle>Order Status</ChartTitle>
            <ChartBox>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={STATUS_COLOURS[index % STATUS_COLOURS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    iconType="circle"
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartBox>
          </CardContainer>

          <CardContainer elevation={0}>
            <ChartTitle>Revenue vs Outstanding</ChartTitle>
            <ChartBox>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={revenueOutstandingData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="colorRevenue"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="colorOutstanding"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#eaeaea"
                  />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    dx={-10}
                    tickFormatter={(val) =>
                      val >= 1000 ? `${val / 1000}k` : val
                    }
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend iconType="plainline" />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    name="Revenue"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                  <Area
                    type="monotone"
                    dataKey="outstanding"
                    name="Outstanding"
                    stroke="#ef4444"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorOutstanding)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartBox>
          </CardContainer>
        </ChartGrid>

        <TableGrid>
          <TableCard elevation={0}>
            <TableHeaderWrapper>
              <ChartTitle noMargin>Recent Orders</ChartTitle>
            </TableHeaderWrapper>
            <ScrollableTableContainer>
              <StyledTable>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>Order #</TableHeaderCell>
                    <TableHeaderCell>Customer</TableHeaderCell>
                    <TableHeaderCell>Date</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell align="right">Amount</TableHeaderCell>
                    <TableHeaderCell align="right">Balance</TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {recentOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableDataCell fontWeight={600}>
                        {order.orderNumber}
                      </TableDataCell>
                      <TableDataCell>{order.customerName}</TableDataCell>
                      <TableDataCell>
                        {dayjs(order.createdAt).format('DD MMM YY')}
                      </TableDataCell>
                      <TableDataCell>
                        <StatusBadge status={order.status}>
                          {order.status}
                        </StatusBadge>
                      </TableDataCell>
                      <TableDataCell align="right" fontWeight={600}>
                        {formatCurrency(order.grandTotal)}
                      </TableDataCell>
                      <TableDataCell
                        align="right"
                        fontWeight={600}
                        colorType={order.balance > 0 ? 'error' : 'default'}
                      >
                        {formatCurrency(order.balance)}
                      </TableDataCell>
                    </TableRow>
                  ))}
                  {recentOrders.length === 0 && (
                    <TableRow>
                      <TableDataCell
                        colSpan={6}
                        align="center"
                        padding="32px"
                        isMuted
                      >
                        No orders found for this period
                      </TableDataCell>
                    </TableRow>
                  )}
                </tbody>
              </StyledTable>
            </ScrollableTableContainer>
          </TableCard>

          <TableCard elevation={0}>
            <TableHeaderWrapper>
              <ChartTitle noMargin>Top Customers</ChartTitle>
            </TableHeaderWrapper>
            <ScrollableTableContainer>
              <StyledTable>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>Customer Name</TableHeaderCell>
                    <TableHeaderCell align="center">Orders</TableHeaderCell>
                    <TableHeaderCell align="right">
                      Total Amount
                    </TableHeaderCell>
                    <TableHeaderCell align="right">Paid</TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {topCustomers.map((customer, idx) => (
                    <TableRow key={idx}>
                      <TableDataCell>{customer.customerName}</TableDataCell>
                      <TableDataCell align="center">
                        {customer.noOfOrders}
                      </TableDataCell>
                      <TableDataCell align="right" fontWeight={600}>
                        {formatCurrency(customer.totalAmount)}
                      </TableDataCell>
                      <TableDataCell
                        align="right"
                        fontWeight={600}
                        colorType="success"
                      >
                        {formatCurrency(customer.paid)}
                      </TableDataCell>
                    </TableRow>
                  ))}
                  {topCustomers.length === 0 && (
                    <TableRow>
                      <TableDataCell
                        colSpan={4}
                        align="center"
                        padding="32px"
                        isMuted
                      >
                        No customer data available
                      </TableDataCell>
                    </TableRow>
                  )}
                </tbody>
              </StyledTable>
            </ScrollableTableContainer>
          </TableCard>
        </TableGrid>

        <TableGrid isReversed>
          <TableCard elevation={0}>
            <TableHeaderWrapper>
              <ChartTitle noMargin>Supplier Payments</ChartTitle>
            </TableHeaderWrapper>
            <ScrollableTableContainer>
              <StyledTable>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>Vendor</TableHeaderCell>
                    <TableHeaderCell>Invoice #</TableHeaderCell>
                    <TableHeaderCell align="right">Paid</TableHeaderCell>
                    <TableHeaderCell align="right">Balance</TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {supplierPayments.map((payment, idx) => (
                    <TableRow key={idx}>
                      <TableDataCell>{payment.vendorName}</TableDataCell>
                      <TableDataCell>{payment.invoiceNumber}</TableDataCell>
                      <TableDataCell align="right" fontWeight={600}>
                        {formatCurrency(payment.paid)}
                      </TableDataCell>
                      <TableDataCell
                        align="right"
                        fontWeight={600}
                        colorType={payment.balance > 0 ? 'error' : 'default'}
                      >
                        {formatCurrency(payment.balance)}
                      </TableDataCell>
                    </TableRow>
                  ))}
                  {supplierPayments.length === 0 && (
                    <TableRow>
                      <TableDataCell
                        colSpan={4}
                        align="center"
                        padding="32px"
                        isMuted
                      >
                        No supplier payments found
                      </TableDataCell>
                    </TableRow>
                  )}
                </tbody>
              </StyledTable>
            </ScrollableTableContainer>
          </TableCard>

          <TableCard elevation={0}>
            <TableHeaderWrapper>
              <ChartTitle noMargin>Recent Vouchers</ChartTitle>
            </TableHeaderWrapper>
            <ScrollableTableContainer>
              <StyledTable>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>Payee</TableHeaderCell>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell align="right">Amount</TableHeaderCell>
                    <TableHeaderCell align="center">Status</TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {recentVouchers.map((voucher, idx) => (
                    <TableRow key={idx}>
                      <TableDataCell fontWeight={600}>
                        {voucher.payee}
                      </TableDataCell>
                      <TableDataCell>{voucher.type}</TableDataCell>
                      <TableDataCell align="right" fontWeight={600}>
                        {formatCurrency(voucher.amount)}
                      </TableDataCell>
                      <TableDataCell align="center">
                        <StatusBadge status={voucher.status}>
                          {voucher.status}
                        </StatusBadge>
                      </TableDataCell>
                    </TableRow>
                  ))}
                  {recentVouchers.length === 0 && (
                    <TableRow>
                      <TableDataCell
                        colSpan={4}
                        align="center"
                        padding="32px"
                        isMuted
                      >
                        No vouchers found
                      </TableDataCell>
                    </TableRow>
                  )}
                </tbody>
              </StyledTable>
            </ScrollableTableContainer>
          </TableCard>
        </TableGrid>
      </DashboardContainer>
    </MainLayout>
  );
};

export default Dashboard;
